import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../assets/contexts/ThemeContext';
import { useRightSidebar } from '../../assets/contexts/RightSidebarContext';
import SidebarCommonList from './SidebarCommonList';
import NotificationSidebarContent from '../../sites/default/pages/popup/NotificationSidebarContent';

/**
 * 사이드바 공통 컴포넌트
 * 우측 사이드바 공통 영역: 장바구니(cart), 알림(notification) 모드 지원
 * Context(useRightSidebar)로 열기/닫기/모드 제어
 */
const SidebarCommon = () => {
  const { t } = useTranslation();
  const sidebarRef = useRef(null);
  const overlayRef = useRef(null);
  const themeContext = useTheme();
  const themeConfig = themeContext?.themeConfig || {};
  const rightSidebarType = themeConfig.rightSidebarType || 'simple';
  const { isOpen, mode, open, close } = useRightSidebar();

  // Context isOpen에 따라 DOM(inside 클래스, overlay), 업무화면 세로스크롤 차단
  useEffect(() => {
    const sidebarEl = sidebarRef.current;
    const wrapper = document.querySelector('.main-wrapper');
    if (!sidebarEl) return;

    if (isOpen) {
      sidebarEl.classList.add('inside');
      if (wrapper) wrapper.classList.add('overlay-active');
      const prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.setAttribute('data-sidebar-open-overflow', prevBodyOverflow || '');
      if (!document.querySelector('.body-overlay')) {
        const overlay = document.createElement('div');
        overlay.className = 'body-overlay';
        overlayRef.current = overlay;
        wrapper?.prepend(overlay);
        overlay.addEventListener('click', () => close());
      }
      if (typeof window.sidebarCommon !== 'undefined' && window.sidebarCommon?.init) {
        window.sidebarCommon.init();
      }
    } else {
      sidebarEl.classList.remove('inside');
      if (wrapper) wrapper.classList.remove('overlay-active');
      const saved = document.body.getAttribute('data-sidebar-open-overflow');
      document.body.style.overflow = saved !== null ? saved : '';
      document.body.removeAttribute('data-sidebar-open-overflow');
    }
  }, [isOpen, close]);

  // 장바구니 버튼 클릭 시 Context로 열기 (cart 모드)
  useEffect(() => {
    const handleCartClick = (e) => {
      e.preventDefault();
      open('cart');
    };
    const registerCartButtons = () => {
      const cartButtons = document.querySelectorAll('.header-action-cart .cart-active, .header-action-cart.cart-active, .nav-toolbar-btn.cart-active');
      cartButtons.forEach(btn => {
        if (!btn.hasAttribute('data-sidebar-common-listener')) {
          btn.setAttribute('data-sidebar-common-listener', 'true');
          btn.addEventListener('click', handleCartClick);
        }
      });
    };
    registerCartButtons();
    const observer = new MutationObserver(registerCartButtons);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      document.querySelectorAll('[data-sidebar-common-listener]').forEach(btn => {
        btn.removeEventListener('click', handleCartClick);
        btn.removeAttribute('data-sidebar-common-listener');
      });
      observer.disconnect();
    };
  }, [open]);

  // rightSidebarType 변경 시 sidebarCommon 초기화
  useEffect(() => {
    let rafId1, rafId2;
    const sidebarElement = sidebarRef.current;
    const wasOpen = sidebarElement?.classList.contains('inside');
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        if (typeof window.sidebarCommon !== 'undefined' && window.sidebarCommon && sidebarRef.current && document.body.contains(sidebarRef.current)) {
          if (typeof window.sidebarCommon.init === 'function') window.sidebarCommon.init();
          if (typeof window.sidebarCommon.loadSidebarType === 'function') window.sidebarCommon.loadSidebarType(rightSidebarType);
          if (wasOpen && sidebarRef.current) {
            sidebarRef.current.classList.add('inside');
            const w = document.querySelector('.main-wrapper');
            if (w) w.classList.add('overlay-active');
          }
        }
      });
    });
    return () => {
      if (rafId1) cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
    };
  }, [rightSidebarType]);

  const handleClose = useCallback((e) => {
    e?.preventDefault();
    close();
  }, [close]);

  // React Portal을 사용하여 body 직하위에 렌더링
  const sidebarContent = (
    <>
      {/* 원본 HTML의 인라인 스타일을 포함 */}
      <style>{`
        /* 기본 스타일 (공통) */
        .sidebar-common-active {
          position: fixed;
          top: 0;
          width: var(--right-sidebar-width, 420px);
          padding: 104px 50px 50px;
          min-height: 100vh;
          max-height: 100vh;
          height: 100vh;
          bottom: 0;
          z-index: 10000;
          right: 0;
          visibility: hidden;
          opacity: 0;
          transition: all 250ms ease-in;
          transform: translate(200px, 0);
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
        }
        .sidebar-common-active.inside {
          visibility: visible;
          opacity: 1;
          z-index: 10000;
          transform: translate(0, 0);
        }

        body.nav-layout-right .sidebar-common-active {
          left: 0;
          right: auto;
          transform: translate(-200px, 0);
        }
        body.nav-layout-right .sidebar-common-active.inside {
          transform: translate(0, 0);
        }

        /* 1) 심플한 컨셉 (Urdan 스타일) */
        .sidebar-common-active.sidebar-type-simple {
          background-color: var(--theme-color-surface, #ffffff);
          border-left: 1px solid color-mix(in srgb, var(--theme-color-primary) 12%, transparent);
          box-shadow: 0 0 87px 0 rgba(0, 0, 0, 0.09);
        }
        body.nav-layout-right .sidebar-common-active.sidebar-type-simple {
          border-left: none;
          border-right: 1px solid color-mix(in srgb, var(--theme-color-primary) 12%, transparent);
        }

        /* 2) 모던한 컨셉 */
        .sidebar-common-active.sidebar-type-modern {
          background: linear-gradient(135deg,
            color-mix(in srgb, var(--theme-color-primary) 4%, var(--theme-color-surface, #ffffff)),
            var(--theme-color-surface, #ffffff)
          );
          border-left: 3px solid var(--theme-color-primary);
          box-shadow:
            0 0 87px 0 color-mix(in srgb, var(--theme-color-primary) 15%, transparent),
            inset -1px 0 0 color-mix(in srgb, var(--theme-color-primary) 8%, transparent);
          padding: 20px;
        }
        body.nav-layout-right .sidebar-common-active.sidebar-type-modern {
          border-left: none;
          border-right: 3px solid var(--theme-color-primary);
          box-shadow:
            0 0 87px 0 color-mix(in srgb, var(--theme-color-primary) 15%, transparent),
            inset 1px 0 0 color-mix(in srgb, var(--theme-color-primary) 8%, transparent);
        }
        .sidebar-common-active.sidebar-type-modern .sidebar-common-header {
          background: linear-gradient(135deg,
            var(--theme-color-primary),
            color-mix(in srgb, var(--theme-color-primary) 85%, transparent)
          );
          border-radius: 8px;
          box-shadow: 0 4px 12px color-mix(in srgb, var(--theme-color-primary) 25%, transparent);
          padding: 6px 14px;
          margin: 0 0 10px;
          min-height: 0;
        }
        .sidebar-common-active.sidebar-type-modern .sidebar-common-header h3 {
          margin: 0;
          padding: 0;
          font-size: 16px;
          line-height: 1.3;
          color: var(--theme-color-on-primary, #ffffff);
        }
        .sidebar-common-active.sidebar-type-modern .sidebar-common-all .cart-content {
          padding-top: 0;
        }
        .sidebar-common-active.sidebar-type-modern .sidebar-common-all .cart-close {
          color: #ffffff;
        }
        .sidebar-common-active.sidebar-type-modern .sidebar-common-all .cart-close:hover {
          color: #ffffff;
        }

        /* 3) 화려한 컨셉 */
        .sidebar-common-active.sidebar-type-vibrant {
          background: linear-gradient(135deg,
            color-mix(in srgb, var(--theme-color-primary) 12%, var(--theme-color-surface, #ffffff)),
            color-mix(in srgb, var(--theme-color-primary) 6%, var(--theme-color-surface, #ffffff))
          );
          border-left: 4px solid var(--theme-color-primary);
          box-shadow:
            0 0 87px 0 color-mix(in srgb, var(--theme-color-primary) 20%, transparent),
            0 8px 32px color-mix(in srgb, var(--theme-color-primary) 12%, transparent),
            inset -2px 0 0 color-mix(in srgb, var(--theme-color-primary) 15%, transparent);
          position: fixed;
        }
        body.nav-layout-right .sidebar-common-active.sidebar-type-vibrant {
          border-left: none;
          border-right: 4px solid var(--theme-color-primary);
          box-shadow:
            0 0 87px 0 color-mix(in srgb, var(--theme-color-primary) 20%, transparent),
            0 8px 32px color-mix(in srgb, var(--theme-color-primary) 12%, transparent),
            inset 2px 0 0 color-mix(in srgb, var(--theme-color-primary) 15%, transparent);
        }
        .sidebar-common-active.sidebar-type-vibrant::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 20% 30%,
            color-mix(in srgb, var(--theme-color-primary) 8%, transparent) 0%,
            transparent 50%);
          pointer-events: none;
          z-index: -1;
        }

        .sidebar-common-active .sidebar-common-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          position: relative;
          flex-shrink: 0;
          margin-bottom: 24px;
          min-height: 0;
        }
        .sidebar-common-active .sidebar-common-header h3 {
          flex: 1;
          min-width: 0;
          font-size: 18px;
          font-weight: 600;
          line-height: 1.3;
        }
        .sidebar-common-active .sidebar-common-all .cart-close {
          position: relative;
          top: auto;
          right: auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          min-width: 34px;
          min-height: 34px;
          padding: 0;
          margin: 0;
          font-size: 28px;
          line-height: 1;
          color: var(--theme-color-text-strong, #1f2d5c);
          background: transparent;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          appearance: none;
          -webkit-appearance: none;
          transition: transform 0.2s ease, color 0.2s ease, background 0.2s ease;
          flex-shrink: 0;
        }
        .sidebar-common-active .sidebar-common-all .cart-close:hover {
          transform: rotate(90deg);
          color: var(--theme-color-primary);
          background: color-mix(in srgb, var(--theme-color-primary) 10%, transparent);
        }
        .sidebar-common-active .sidebar-common-all .cart-close:focus {
          outline: none;
        }
        .sidebar-common-active .sidebar-common-all .cart-close:focus-visible {
          outline: 2px solid var(--theme-color-primary);
          outline-offset: 2px;
        }
        .sidebar-common-active .sidebar-common-all .cart-close i {
          font-size: inherit;
          color: inherit;
        }
        .sidebar-common-active .sidebar-common-all {
          position: relative;
          z-index: 1;
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .sidebar-common-active .sidebar-common-all .cart-content {
          flex: 1;
          min-height: 0;
          height: 100%;
          overflow-y: auto;
          overflow-x: hidden;
          position: relative;
          z-index: 1;
          -webkit-overflow-scrolling: touch;
        }
        .sidebar-common-active .sidebar-common-header h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          line-height: 1.3;
          color: var(--theme-color-text-strong, #1f2d5c);
        }
        .sidebar-common-active .sidebar-common-all .cart-content {
          margin-top: 0;
        }
        .sidebar-common-active .sidebar-common-content-area {
          min-height: 200px;
        }

        /* 모바일/태블릿: 알림 팝업 반응형 */
        @media (max-width: 991.98px) {
          .sidebar-common-active {
            width: 100% !important;
            max-width: 100vw;
            min-height: 100vh;
            min-height: 100dvh;
            max-height: 100vh;
            max-height: 100dvh;
            height: 100%;
            padding: 56px 16px 24px;
            padding-top: max(56px, env(safe-area-inset-top, 0px));
            padding-left: max(16px, env(safe-area-inset-left, 0px));
            padding-right: max(16px, env(safe-area-inset-right, 0px));
            padding-bottom: max(24px, env(safe-area-inset-bottom, 0px));
            transform: translate(100%, 0);
            -webkit-overflow-scrolling: touch;
            box-sizing: border-box;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }
          .sidebar-common-active.inside {
            transform: translate(0, 0);
          }
          body.nav-layout-right .sidebar-common-active {
            transform: translate(-100%, 0);
          }
          body.nav-layout-right .sidebar-common-active.inside {
            transform: translate(0, 0);
          }
          .sidebar-common-active .sidebar-common-all {
            display: flex;
            flex-direction: column;
            min-height: 0;
            flex: 1;
          }
          .sidebar-common-active .sidebar-common-all .cart-close {
            width: 34px;
            height: 34px;
            min-width: 34px;
            min-height: 34px;
            font-size: 22px;
            flex-shrink: 0;
          }
          .sidebar-common-active .sidebar-common-all .cart-content {
            padding-right: 4px;
            -webkit-overflow-scrolling: touch;
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            overflow-x: hidden;
            max-height: none;
          }
          .sidebar-common-active .sidebar-common-header {
            margin-bottom: 20px;
          }
          .sidebar-common-active .sidebar-common-header h3 {
            font-size: 16px;
            padding-right: 0;
          }
          .sidebar-common-active .sidebar-common-content-area {
            min-height: 120px;
          }
          .sidebar-common-active .sidebar-common-notification {
            min-height: 0;
            display: flex;
            flex-direction: column;
          }
          .sidebar-common-active.sidebar-type-modern {
            padding: 12px;
            padding-top: max(12px, env(safe-area-inset-top, 0px));
          }
        }
        @media (max-width: 575.98px) {
          .sidebar-common-active {
            padding-left: max(12px, env(safe-area-inset-left, 0px));
            padding-right: max(12px, env(safe-area-inset-right, 0px));
            padding-bottom: max(20px, env(safe-area-inset-bottom, 0px));
          }
          .sidebar-common-active .sidebar-common-header h3 {
            font-size: 15px;
          }
        }
      `}</style>
      <div
        ref={sidebarRef}
        className={`sidebar-common-active sidebar-type-${rightSidebarType}`}
        style={{
          width: themeConfig.rightSidebarWidth ? `${themeConfig.rightSidebarWidth}px` : undefined
        }}
      >
        <div className="sidebar-common-all">
          <div className="sidebar-common-header">
            <h3>
              {mode === 'notification'
                ? t('popup.sidebarCommon.notificationTitle')
                : t('popup.sidebarCommon.cartTitle')}
            </h3>
            <button
              className="cart-close"
              type="button"
              onClick={handleClose}
              aria-label={t('popup.sidebarCommon.closeAria')}
            >
              <i className="pe-7s-close"></i>
            </button>
          </div>
          <div className="cart-content">
            <div id="sidebar-common-content-area" className="sidebar-common-content-area">
              {mode === 'notification' ? (
                <div className="sidebar-common-notification">
                  <NotificationSidebarContent />
                </div>
              ) : (
                <SidebarCommonList />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // body 직하위에 렌더링 (React Portal 사용)
  return createPortal(sidebarContent, document.body);
};

export default SidebarCommon;
